import { useEffect, useMemo, useState } from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useNavigate, useSearchParams } from "react-router-dom"
import {
  createTicket,
  fetchTickets,
  TICKET_ART_LABEL,
  TICKET_STATUS_LABEL,
  type Ticket,
  type TicketArt,
  type TicketFilter,
  type TicketStatus,
} from "@/lib/api"
import { Button, Input, Select } from "@/components/ui"
import { DataGrid, type ColumnDef } from "@/components/DataGrid"
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SHEET_WIDTH,
} from "@/components/Sheet"
import { fehlerNachricht, useToast } from "@/lib/toast"
import { TicketDetailContent } from "./TicketDetailPage"

const STATUS_OPTIONEN: TicketStatus[] = [
  "neu",
  "offen",
  "in_arbeit",
  "warten",
  "erledigt",
  "zurueckgewiesen",
]
const ART_OPTIONEN: TicketArt[] = ["wartung_inspektion", "pruefung", "aufgabe", "ereignis", "auftrag"]

/** Anzeige-Labels der Ticket-Quelltypen (ticket_service._SOURCE_MODELS) */
const QUELLE_LABEL: Record<string, string> = {
  wartung: "Wartung",
  pruefung: "Prüfung",
  aufgabe: "KVP-Aufgabe",
  ereignis: "Ereignis",
  komponente: "Komponente",
}

/**
 * Ticket-Liste mit Filtern (AP-4.3): Status/Art/Priorität, Volltextsuche
 * (G.12-Äquivalent) und "Meine Tickets" (G.13 — echter Query-Parameter auf
 * der Mitarbeiterzuordnung, KEINE Pro-Nutzer-Tabelle, ARCHITEKTUR.md E4).
 */
export function TicketsPage() {
  const [filter, setFilter] = useState<TicketFilter>({})
  const [neueBeschreibung, setNeueBeschreibung] = useState("")
  const [sheetOffen, setSheetOffen] = useState(false)
  const [detailTicketId, setDetailTicketId] = useState<number | null>(null)
  const queryClient = useQueryClient()
  const toast = useToast()
  const navigate = useNavigate()

  // Quellen-Vorbelegung per URL (CommandPalette „Ticket anlegen"):
  // /tickets?quelle=<ereignis|aufgabe|wartung|pruefung>&quelleId=<id>
  const [searchParams, setSearchParams] = useSearchParams()
  const quelleTyp = searchParams.get("quelle")
  const quelleId = searchParams.get("quelleId")
  // ?zurueck=<pfad>: nach dem Anlegen dorthin zurückkehren (Issue #5 —
  // „+ Neu" im Tickets-Reiter der Komponentenansicht)
  const zurueck = searchParams.get("zurueck")
  const quelleGueltig = quelleTyp !== null && quelleTyp in QUELLE_LABEL && quelleId !== null
  useEffect(() => {
    if (quelleGueltig) setSheetOffen(true)
  }, [quelleGueltig])
  const quelleEntfernen = () => setSearchParams({}, { replace: true })

  const { data: tickets, isLoading, isError, refetch } = useQuery({
    queryKey: ["tickets", filter],
    queryFn: () => fetchTickets(filter),
  })

  const anlegen = useMutation({
    mutationFn: () =>
      createTicket({
        kurzbeschreibung: neueBeschreibung,
        // Quelle aus der URL-Vorbelegung — sonst manuelles Ticket
        ...(quelleGueltig
          ? { sourceType: quelleTyp!, sourceId: Number(quelleId) }
          : {}),
      }),
    onSuccess: () => {
      setNeueBeschreibung("")
      setSheetOffen(false)
      queryClient.invalidateQueries({ queryKey: ["tickets"] })
      toast.success("Ticket angelegt.")
      if (zurueck) navigate(zurueck)
      else quelleEntfernen()
    },
    onError: (err) => toast.error(fehlerNachricht(err, "Ticket konnte nicht angelegt werden.")),
  })

  // Grid-Spalten: header als String, damit die Spalten-Auswahl ihn anzeigen kann
  const spalten = useMemo<ColumnDef<Ticket>[]>(
    () => [
      {
        id: "ticketKey",
        header: "Ticket-Key",
        accessorFn: (t) => t.ticketKey ?? `#${t.id}`,
        cell: ({ row }) => row.original.ticketKey ?? `#${row.original.id}`,
        meta: { cellClassName: "font-mono text-xs" },
      },
      {
        id: "kurzbeschreibung",
        header: "Kurzbeschreibung",
        accessorFn: (t) => t.kurzbeschreibung,
        cell: ({ row }) => row.original.kurzbeschreibung,
        meta: { cellClassName: "text-slate-900" },
      },
      {
        id: "art",
        header: "Art",
        accessorFn: (t) => TICKET_ART_LABEL[t.art],
        cell: ({ row }) => TICKET_ART_LABEL[row.original.art],
      },
      {
        id: "status",
        header: "Status",
        accessorFn: (t) => TICKET_STATUS_LABEL[t.status],
        cell: ({ row }) => <StatusBadge status={row.original.status} />,
      },
      {
        id: "prioritaet",
        header: "Priorität",
        accessorFn: (t) => t.prioritaet ?? "",
        cell: ({ row }) => row.original.prioritaet ?? "—",
      },
    ],
    [],
  )

  return (
    <div>
      <header className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Servicetickets</h1>
          <p className="mt-1 text-sm text-slate-600">
            Ticket-Hub mit polymorpher Herkunft (Wartung/Prüfung/Aufgabe/Ereignis/manuell).
          </p>
        </div>
        <Button type="button" onClick={() => setSheetOffen(true)}>
          + Neues Ticket
        </Button>
      </header>

      <Sheet
        open={sheetOffen}
        onOpenChange={(offen) => {
          setSheetOffen(offen)
          if (!offen) quelleEntfernen() // Abbruch: Vorbelegung verwerfen
        }}
      >
        <SheetContent sheetKey="ticket-neu" defaultWidth={SHEET_WIDTH.md}>
          <SheetHeader>
            <SheetTitle>Neues Ticket</SheetTitle>
            <SheetDescription>
              {quelleGueltig
                ? `Serviceticket zu ${QUELLE_LABEL[quelleTyp!]} #${quelleId} anlegen.`
                : "Manuelles Servicticket (Auftrag) anlegen."}
            </SheetDescription>
          </SheetHeader>
          {quelleGueltig && (
            <div className="mb-4 flex items-center justify-between rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700">
              <span>
                Quelle: <span className="font-medium">{QUELLE_LABEL[quelleTyp!]} #{quelleId}</span>
                {" — "}Anlage/Komponente werden aus der Quelle übernommen.
              </span>
              <button
                type="button"
                onClick={quelleEntfernen}
                title="Ohne Quelle anlegen (manuelles Ticket)"
                className="ml-3 shrink-0 rounded-md text-slate-400 hover:text-slate-700"
              >
                ✕
              </button>
            </div>
          )}
          <form
            className="grid gap-4"
            onSubmit={(e) => {
              e.preventDefault()
              if (neueBeschreibung.trim()) anlegen.mutate()
            }}
          >
            <div className="space-y-1.5">
              <label htmlFor="ticket-kurzbeschreibung" className="block text-sm text-slate-700">
                Kurzbeschreibung
              </label>
              <Input
                id="ticket-kurzbeschreibung"
                type="text"
                value={neueBeschreibung}
                onChange={(e) => setNeueBeschreibung(e.target.value)}
                placeholder="Was ist zu tun?"
                required
              />
            </div>
            <SheetFooter>
              <Button type="button" variant="secondary" onClick={() => setSheetOffen(false)}>
                Abbrechen
              </Button>
              <Button type="submit" disabled={anlegen.isPending || !neueBeschreibung.trim()}>
                {anlegen.isPending ? "Speichert …" : "Speichern"}
              </Button>
            </SheetFooter>
          </form>
        </SheetContent>
      </Sheet>

      {/* Detail-Sheet von rechts (UX_KONZEPT.md §7) — Zeilen-Klick öffnet statt zu
          navigieren; `/tickets/:id` bleibt als Deep-Link-Fallback (TicketDetailPage). */}
      <Sheet open={detailTicketId !== null} onOpenChange={(open) => !open && setDetailTicketId(null)}>
        <SheetContent className="overflow-y-auto" sheetKey="ticket-detail" defaultWidth={SHEET_WIDTH.xl}>
          {detailTicketId !== null && <TicketDetailContent ticketId={detailTicketId} />}
        </SheetContent>
      </Sheet>

      <div className="mb-4 flex flex-wrap items-center gap-2 text-sm">
        <Input
          type="text"
          placeholder="Suche…"
          value={filter.suche ?? ""}
          onChange={(e) => setFilter((f) => ({ ...f, suche: e.target.value || undefined }))}
          className="h-auto w-auto py-1"
        />
        <Select
          value={filter.status ?? ""}
          onChange={(e) => setFilter((f) => ({ ...f, status: e.target.value || undefined }))}
          className="h-auto w-auto py-1"
        >
          <option value="">Alle Status</option>
          {STATUS_OPTIONEN.map((s) => (
            <option key={s} value={s}>
              {TICKET_STATUS_LABEL[s]}
            </option>
          ))}
        </Select>
        <Select
          value={filter.art ?? ""}
          onChange={(e) => setFilter((f) => ({ ...f, art: e.target.value || undefined }))}
          className="h-auto w-auto py-1"
        >
          <option value="">Alle Arten</option>
          {ART_OPTIONEN.map((a) => (
            <option key={a} value={a}>
              {TICKET_ART_LABEL[a]}
            </option>
          ))}
        </Select>
        <label className="flex items-center gap-1.5 rounded-md border border-slate-300 px-2 py-1">
          <input
            type="checkbox"
            checked={filter.meine ?? false}
            onChange={(e) => setFilter((f) => ({ ...f, meine: e.target.checked || undefined }))}
          />
          Meine Tickets
        </label>
        {(filter.status || filter.art || filter.suche || filter.meine) && (
          <button
            type="button"
            className="text-slate-500 underline hover:text-slate-800"
            onClick={() => setFilter({})}
          >
            Filter zurücksetzen
          </button>
        )}
      </div>

      {isLoading && (
        <div className="animate-pulse space-y-2" aria-label="Lade Tickets …">
          <div className="h-8 rounded-md bg-slate-100" />
          <div className="h-8 rounded-md bg-slate-100" />
          <div className="h-8 rounded-md bg-slate-100" />
        </div>
      )}
      {isError && (
        <p className="text-sm text-red-700">
          Tickets konnten nicht geladen werden.{" "}
          <button type="button" onClick={() => refetch()} className="underline hover:no-underline">
            Erneut versuchen
          </button>
        </p>
      )}

      {tickets && (
        <DataGrid
          columns={spalten}
          data={tickets}
          storageKey="tickets"
          onRowClick={(t) => setDetailTicketId(t.id)}
          emptyMessage={
            filter.status || filter.art || filter.suche || filter.meine
              ? "Keine Tickets für diese Filter gefunden."
              : `Noch keine Tickets — mit „+ Neues Ticket" oben ein neues Ticket anlegen.`
          }
        />
      )}
    </div>
  )
}

const STATUS_STYLES: Record<TicketStatus, string> = {
  neu: "bg-slate-100 text-slate-700",
  offen: "bg-blue-100 text-blue-800",
  in_arbeit: "bg-orange-100 text-orange-800",
  warten: "bg-yellow-100 text-yellow-800",
  erledigt: "bg-green-100 text-green-800",
  // Zählt fachlich als "offen" (OFFENE_PUNKTE #8.3), optisch trotzdem abgesetzt
  zurueckgewiesen: "bg-red-100 text-red-800",
}

export function StatusBadge({ status }: { status: TicketStatus }) {
  return (
    <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_STYLES[status]}`}>
      {TICKET_STATUS_LABEL[status]}
    </span>
  )
}
